import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Image as ImageIcon, Wand2, RefreshCw, Loader2, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

/**
 * ImageGeneratorCard - UI for generating/regenerating blog post featured images
 * Shows current image if exists, allows custom prompt input
 */
const ImageGeneratorCard = ({ postId, currentImageUrl, postTitle, onImageGenerated }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState(null);

  const handleGenerateImage = async (useCustom = false) => {
    if (!postId) {
      toast.error('Save the post first before generating an image');
      return;
    }

    setIsGenerating(true);
    
    try {
      const token = localStorage.getItem('admin_token');
      const payload = useCustom && customPrompt.trim() 
        ? { custom_prompt: customPrompt.trim() }
        : {};
      
      const response = await axios.post(
        `${API}/admin/blog/posts/${postId}/generate-image`,
        payload,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: useCustom && customPrompt.trim() ? { custom_prompt: customPrompt.trim() } : {}
        }
      );
      
      if (response.data.success) {
        setGeneratedUrl(response.data.image_url);
        toast.success('Image generated successfully!');
        
        // Notify parent component
        if (onImageGenerated) {
          onImageGenerated(response.data.image_url);
        }
      }
    } catch (error) {
      console.error('Image generation error:', error);
      const message = error.response?.data?.detail || 'Failed to generate image';
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const displayImageUrl = generatedUrl || currentImageUrl;
  const absoluteImageUrl = displayImageUrl 
    ? (displayImageUrl.startsWith('http') ? displayImageUrl : `${window.location.origin}${displayImageUrl}`)
    : null;

  return (
    <Card data-testid="image-generator-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          Featured Image
          {displayImageUrl && (
            <Badge variant="outline" className="ml-auto text-xs">
              <Check className="w-3 h-3 mr-1" />
              Has Image
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current/Generated Image Preview */}
        {absoluteImageUrl ? (
          <div className="relative aspect-video rounded-lg overflow-hidden bg-muted border">
            <img 
              src={absoluteImageUrl}
              alt="Featured"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            {generatedUrl && (
              <div className="absolute top-2 right-2">
                <Badge className="bg-green-500">New</Badge>
              </div>
            )}
          </div>
        ) : (
          <div className="aspect-video rounded-lg bg-muted border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground">
            <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
            <p className="text-sm">No featured image</p>
            <p className="text-xs">Click below to generate one</p>
          </div>
        )}

        {/* Generation Buttons */}
        <div className="space-y-3">
          <Button
            onClick={() => handleGenerateImage(false)}
            disabled={isGenerating || !postId}
            className="w-full"
            data-testid="generate-image-btn"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : displayImageUrl ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate Image
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4 mr-2" />
                Generate Image
              </>
            )}
          </Button>

          {/* Custom Prompt Toggle */}
          <div className="pt-2 border-t">
            <button
              type="button"
              onClick={() => setShowCustomPrompt(!showCustomPrompt)}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <Wand2 className="w-3 h-3" />
              {showCustomPrompt ? 'Hide custom prompt' : 'Use custom prompt'}
            </button>
          </div>

          {/* Custom Prompt Input */}
          {showCustomPrompt && (
            <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
              <Label htmlFor="custom-prompt" className="text-sm">
                Custom Image Prompt
              </Label>
              <Textarea
                id="custom-prompt"
                placeholder="E.g., Professional photograph of a diverse team in a modern meeting room reviewing documents, natural lighting, business casual attire..."
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                rows={3}
                className="text-sm"
                data-testid="custom-prompt-input"
              />
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>
                  Describe a realistic workplace scene. The system will automatically add 
                  professional photography styling. Avoid requesting illustrations or artistic styles.
                </span>
              </div>
              <Button
                onClick={() => handleGenerateImage(true)}
                disabled={isGenerating || !postId || !customPrompt.trim()}
                variant="secondary"
                size="sm"
                className="w-full"
                data-testid="generate-custom-image-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate with Custom Prompt
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Help Text */}
        <p className="text-xs text-muted-foreground">
          Images are generated using AI with professional documentary-style photography. 
          Generation typically takes 10-30 seconds.
        </p>
      </CardContent>
    </Card>
  );
};

export default ImageGeneratorCard;
